import categoryAssets from '@/assets/skins_assets';

// Kept for legacy callers — real gating now lives on tournament.tournamentSettings.skinThemeEnabled.
export const SKIN_FEATURE_FLAG = 'TOURNAMENT_SKIN_THEME';

// Backend theme codes seeded in liquibase 20260407120000_competition_skin_themes.xml:
// WC26, F1, TENNIS, NBA, INVESTORS, SINGERS.
// Local asset folders are keyed under those same codes via this map.
const BACKEND_CODE_TO_ASSET_KEY = {
  WC26: 'WC26',
  F1: 'F1',
  TENNIS: 'TENNIS',
  NBA: 'NBA',
  INVESTORS: 'INVESTORS',
  SINGERS: 'SINGERS',
};

// Country fallback by character displayName. Backend CompetitionSkinCharacter doesn't
// carry a country field yet — when it does, delete this map and read skin.country from the DTO.
const CHARACTER_COUNTRY_FALLBACK = {
  'Lionel Messi': 'Argentina',
  'Cristiano Ronaldo': 'Portugal',
  'Kylian Mbappé': 'France',
  'Erling Haaland': 'Norway',
  'Vinícius Jr.': 'Brazil',
  'Mohamed Salah': 'Egypt',
  'Harry Kane': 'United Kingdom',
  'Robert Lewandowski': 'Poland',
  'Neymar': 'Brazil',
  'Ronaldinho': 'Brazil',
  'Max Verstappen': 'Netherlands',
  'Lando Norris': 'United Kingdom',
  'Lewis Hamilton': 'United Kingdom',
  'Fernando Alonso': 'Spain',
  'Michael Schumacher': 'Germany',
  'Ayrton Senna': 'Brazil',
  'Sebastian Vettel': 'Germany',
  'LeBron James': 'United States',
  'Stephen Curry': 'United States',
  'Kevin Durant': 'United States',
  'Nikola Jokić': 'Serbia',
  'Giannis Antetokounmpo': 'Greece',
  'Luka Dončić': 'Slovenia',
  'Michael Jordan': 'United States',
  'Kobe Bryant': 'United States',
  'Carlos Alcaraz': 'Spain',
  'Jannik Sinner': 'Italy',
  'Novak Djokovic': 'Serbia',
  'Aryna Sabalenka': 'Belarus',
  'Coco Gauff': 'United States',
  'Rafael Nadal': 'Spain',
  'Roger Federer': 'Switzerland',
  'Serena Williams': 'United States',
  'Warren Buffett': 'United States',
  'Charlie Munger': 'United States',
  'George Soros': 'Hungary',
  'Cathie Wood': 'United States',
  'Peter Thiel': 'United States',
  'Mark Cuban': 'United States',
  'Masayoshi Son': 'Japan',
  'Taylor Swift': 'United States',
  'Bad Bunny': 'Puerto Rico',
  'Beyoncé': 'United States',
  'The Weeknd': 'Canada',
  'Shakira': 'Colombia',
  'Dua Lipa': 'United Kingdom',
  'Adele': 'United Kingdom',
  'Bruno Mars': 'United States',
};

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/);
  if (!parts[0]) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function getThemeCode(tournament) {
  if (!tournament) return null;
  return (
    tournament.competitionSkinTheme?.code
    || tournament.tournamentSettings?.skinTheme?.code
    || null
  );
}

export function getCategoryAssets(themeCode) {
  if (!themeCode) return null;
  const normalized = String(themeCode).toUpperCase();
  const key = BACKEND_CODE_TO_ASSET_KEY[normalized] || normalized;
  return categoryAssets[key] || null;
}

export function isSkinEnabled(tournament) {
  if (!tournament) return false;
  const enabled = tournament.competitionSkinTheme?.enabled
    ?? tournament.tournamentSettings?.skinThemeEnabled
    ?? false;
  return !!enabled && !!getThemeCode(tournament);
}

/**
 * Build the UI-facing `skin` object from the server's per-participant fields.
 * Returns null when the participant has no assigned skin.
 */
export function buildSkinFromServer(participant, themeCode) {
  if (!participant) return null;
  const name = participant.skinCharacterName || participant.skinCharacterCode;
  const imageUrl = participant.skinImageUrl;
  if (!name && !imageUrl) return null;
  const { firstName, lastName } = splitName(name || '');
  const assets = getCategoryAssets(themeCode);
  return {
    name: name || '',
    firstName,
    lastName,
    country: CHARACTER_COUNTRY_FALLBACK[name] || null,
    code: participant.skinCharacterCode || null,
    category: themeCode || null,
    sourceImage: imageUrl || null,
    imageUrl: imageUrl || (assets ? assets.charMock : null),
  };
}

export function hydrateParticipants(tournament) {
  if (!isSkinEnabled(tournament)) return tournament;
  if (!Array.isArray(tournament.participants)) return tournament;
  const themeCode = getThemeCode(tournament);
  tournament.participants.forEach((p) => {
    if (!p || p.skin) return;
    const skin = buildSkinFromServer(p, themeCode);
    if (skin) p.skin = skin;
  });
  tournament.participants = [...tournament.participants];
  return tournament;
}

export default {
  SKIN_FEATURE_FLAG,
  isSkinEnabled,
  getThemeCode,
  getCategoryAssets,
  buildSkinFromServer,
  hydrateParticipants,
};
